import { useEffect } from 'react'
import { type LayoutChangeEvent, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import Typography from '@ui/typography'
import { leadingNone } from '@ui/typography/styles'
import { useCurrencySign, usePriceFormatter } from 'hooks/useCurrency'
import cn from '@ui/utils/cn'
import { colors } from '@ui/colors'
import {
  PRICE_BADGE_PADDING_X,
  formatBadgePrice,
  getPriceInnerContentWidth,
  getPriceWidth,
} from './utils'

const WIDTH_ANIMATION_MS = 250

interface Props {
  value: number
}

export default function PayNowPriceBadge({ value }: Props) {
  const currencySign = useCurrencySign()
  const formatter = usePriceFormatter()
  const price = formatBadgePrice(formatter, value, currencySign)
  const { amount, currency, currencyFirst } = price
  const formatted = `${amount} ${currency}`
  const targetWidth = getPriceWidth(price)
  const innerContentWidth = getPriceInnerContentWidth(price)
  const width = useSharedValue(targetWidth)
  const estimatedContentWidth = useSharedValue(innerContentWidth)
  const measuredContentWidth = useSharedValue(innerContentWidth)
  const contentLayoutVersion = useSharedValue(0)
  const measuredLayoutVersion = useSharedValue(0)
  const isFilled = value <= 0
  const textColor = isFilled ? colors.white : colors.text

  useEffect(() => {
    estimatedContentWidth.set(innerContentWidth)
    contentLayoutVersion.set(contentLayoutVersion.value + 1)
    width.set(withTiming(targetWidth, { duration: WIDTH_ANIMATION_MS }))
  }, [innerContentWidth, targetWidth])

  const handleContentLayout = (event: LayoutChangeEvent) => {
    const contentWidth = event.nativeEvent.layout.width
    measuredContentWidth.set(contentWidth)
    measuredLayoutVersion.set(contentLayoutVersion.value)

    // Estimates miss wider signs and extra decimals; fit the real content
    const measuredWidth = contentWidth + PRICE_BADGE_PADDING_X * 2
    if (Math.abs(measuredWidth - targetWidth) > 1) {
      width.set(withTiming(measuredWidth, { duration: WIDTH_ANIMATION_MS }))
    }
  }

  const animatedStyle = useAnimatedStyle(() => ({
    width: width.value,
  }))

  const contentStyle = useAnimatedStyle(() => {
    const badgeWidth = width.value
    const hasCurrentMeasurement =
      measuredLayoutVersion.value === contentLayoutVersion.value
    const contentWidth = hasCurrentMeasurement
      ? measuredContentWidth.value
      : estimatedContentWidth.value
    const minBadgeWidth = contentWidth + PRICE_BADGE_PADDING_X * 2

    if (badgeWidth >= minBadgeWidth) {
      return {
        width: badgeWidth,
        paddingHorizontal: PRICE_BADGE_PADDING_X,
        justifyContent: 'center' as const,
        transform: [{ translateX: 0 }],
      }
    }

    return {
      width: badgeWidth,
      paddingHorizontal: 0,
      justifyContent: 'flex-start' as const,
      transform: [
        {
          translateX: badgeWidth - PRICE_BADGE_PADDING_X - contentWidth,
        },
      ],
    }
  })

  return (
    <Animated.View
      style={animatedStyle}
      className={cn(
        'h-6 overflow-hidden rounded-full',
        isFilled ? 'bg-white/25' : 'bg-text/10'
      )}
    >
      <Animated.View
        style={contentStyle}
        className="absolute inset-y-0 left-0 flex-row items-center"
      >
        <View
          key={formatted}
          onLayout={handleContentLayout}
          className={cn(
            'flex-row items-center gap-[5px]',
            currencyFirst && 'flex-row-reverse'
          )}
        >
          <Typography
            variant="MDB"
            numberOfLines={1}
            style={{ color: textColor }}
            className={cn('whitespace-nowrap', leadingNone)}
          >
            {amount}
          </Typography>
          <Typography
            variant="MDB"
            numberOfLines={1}
            style={{ color: textColor }}
            className={cn('shrink-0 whitespace-nowrap', leadingNone)}
          >
            {currency}
          </Typography>
        </View>
      </Animated.View>
    </Animated.View>
  )
}
